import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Chat.css';
import './Login.css';
import './transitions.css';
import { graspApi, webchatApi } from './api';
import { ArrowPathIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { buildChatTheme, bubbleRadiusClass } from './chatTheme';
import LoginFooter from './LoginFooter';
import { capturePendingText } from './pendingText';
import ChannelAvatar from './ChannelAvatar';
import ThemeToggle from './ThemeToggle';
import { LoginPageSkeleton } from './Skeleton';
import { CHANNEL_STATES, getChannelFailure } from './channelStatus';
import {
  maskEmail,
  normalizeAndValidateEmail,
  normalizeOtpCode,
} from './auth/emailAuth';

const RESEND_COOLDOWN_SECONDS = 45;
const EMAIL_MAX_LENGTH = 254;

function Login() {
  const [username, setUsername] = useState('');
  const {
    login,
    token,
    username: sessionUser,
    bootstrapped,
    continuePrompt,
    continueAs,
    dismissContinue,
  } = useAuth();
  const navigate = useNavigate();
  const { id, channel } = useParams();
  const [channelState, setChannelState] = useState(CHANNEL_STATES.LOADING);
  const [channelFailure, setChannelFailure] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [channelInfo, setChannelInfo] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsHighlight, setTermsHighlight] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [step, setStep] = useState('identity'); // identity | otp | continue
  const [challengeId, setChallengeId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const [continueBusy, setContinueBusy] = useState(false);

  const theme = buildChatTheme(channelInfo);
  const channelName = channelInfo?.channel_name || channel || 'Chat';
  const emailValidation = normalizeAndValidateEmail(username);
  const hasValidEmail = emailValidation.ok;
  const canSubmitIdentity =
    channelState === CHANNEL_STATES.READY &&
    !isSubmitting &&
    acceptedTerms &&
    hasValidEmail;
  const canSubmitOtp =
    channelState === CHANNEL_STATES.READY &&
    !isSubmitting &&
    otpCode.length === 6;

  const stepHint =
    step === 'otp'
      ? `Enter the 6-digit code we sent to ${
          pendingEmail ? maskEmail(pendingEmail) : 'your email'
        }.`
      : !acceptedTerms && !username.trim()
        ? 'Accept the terms, then enter your email address to continue.'
        : !acceptedTerms
          ? 'Tick the box above to accept the Terms and Conditions.'
          : !username.trim()
            ? 'Enter your email address, then tap send.'
            : !hasValidEmail
              ? 'Enter a valid email address.'
              : '';

  const nudgeIncomplete = () => {
    if (step === 'otp') {
      setFormError('Enter the 6-digit code from your email.');
      return;
    }
    if (!acceptedTerms) {
      setTermsHighlight(true);
      setFormError('Please accept the Terms and Conditions to continue.');
      document.getElementById('login-terms')?.focus();
      return;
    }
    if (!hasValidEmail) {
      setFormError(
        emailValidation.error || 'Enter a valid email address to continue.'
      );
    }
  };

  // Campaign links: stash ?text= before any redirect can drop it.
  useEffect(() => {
    capturePendingText(id, channel, window.location.search);
  }, [id, channel]);

  useEffect(() => {
    if (!bootstrapped) return;
    if (continuePrompt?.require_continue_prompt) {
      setStep('continue');
      return;
    }
    if (token && sessionUser) {
      navigate(`/chat/${id}/${channel}`, { replace: true });
    }
  }, [
    bootstrapped,
    continuePrompt,
    token,
    sessionUser,
    id,
    channel,
    navigate,
  ]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    let cancelled = false;

    const validateChannel = async () => {
      try {
        const response = await graspApi.get(`/v2/channels/${id}/${channel}/`);

        if (cancelled) return;

        if (response.status === 200) {
          setChannelState(CHANNEL_STATES.READY);
          setChannelFailure(null);
        } else {
          setChannelState(CHANNEL_STATES.NOT_FOUND);
          setChannelFailure({
            state: CHANNEL_STATES.NOT_FOUND,
            title: 'This chat link is not available',
            message:
              'The channel may have been removed, or the link may be incorrect.',
            canRetry: false,
          });
        }
      } catch (error) {
        if (cancelled) return;
        const failure = getChannelFailure(error);
        setChannelState(failure.state);
        setChannelFailure(failure);
      }
    };

    const fetchBrand = async () => {
      try {
        const response = await graspApi.get(
          `/v2/claim/channel/webchat/${id}/${channel}/`
        );
        if (!cancelled) setChannelInfo(response.data);
      } catch {
        if (!cancelled) setChannelInfo({ channel_name: channel });
      }
    };

    setIsBootstrapping(true);
    setFormError('');
    setChannelState(CHANNEL_STATES.LOADING);
    setChannelFailure(null);
    setAcceptedTerms(false);
    setTermsHighlight(false);

    Promise.all([validateChannel(), fetchBrand()]).finally(() => {
      if (!cancelled) setIsBootstrapping(false);
    });

    return () => {
      cancelled = true;
    };
  }, [id, channel, retryAttempt]);

  const requestOtp = async (email) => {
    const response = await webchatApi.post('/auth/otp/request/', {
      email,
      channel_id: String(id || '').slice(0, 64),
      channel_name: String(channel || '').slice(0, 120),
    });
    setChallengeId(response.data.challenge_id || '');
    setPendingEmail(response.data.email || email);
    setResendIn(RESEND_COOLDOWN_SECONDS);
    setStep('otp');
    setOtpCode('');
  };

  const handleIdentitySubmit = async (e) => {
    e.preventDefault();
    if (channelState !== CHANNEL_STATES.READY || isSubmitting) {
      if (channelState !== CHANNEL_STATES.READY) {
        setFormError('This chat is not available right now.');
      }
      return;
    }
    if (!acceptedTerms || !hasValidEmail) {
      nudgeIncomplete();
      return;
    }

    setIsSubmitting(true);
    setFormError('');
    try {
      await requestOtp(emailValidation.email);
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        const retry = error.response?.headers?.['retry-after'];
        setFormError(
          retry
            ? `Too many requests. Try again in ${retry}s.`
            : 'Too many requests. Try again later.'
        );
        if (retry) setResendIn(Number(retry) || RESEND_COOLDOWN_SECONDS);
      } else if (status === 404) {
        setFormError(
          'Auth API is unavailable. Restart the webchat backend so /api/auth/ is loaded.'
        );
      } else if (status === 400) {
        setFormError('Enter a valid email address.');
      } else {
        setFormError("We couldn't send a code. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const code = normalizeOtpCode(otpCode);
    if (code.length !== 6 || !challengeId) {
      nudgeIncomplete();
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      const response = await webchatApi.post('/auth/otp/verify/', {
        challenge_id: String(challengeId).slice(0, 128),
        code,
      });
      login(response.data.access, response.data.username, {
        lastSeenAt: response.data.last_seen_at,
      });
      navigate(`/chat/${id}/${channel}`);
    } catch (error) {
      const status = error.response?.status;
      setFormError(
        typeof error.response?.data?.detail === 'string'
          ? error.response.data.detail
          : status === 429
            ? 'Too many attempts. Try again later.'
            : 'That code did not work. Try again.'
      );
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || isSubmitting || !pendingEmail) return;
    const validated = normalizeAndValidateEmail(pendingEmail);
    if (!validated.ok) {
      setFormError('Enter a valid email address.');
      setStep('identity');
      return;
    }
    setIsSubmitting(true);
    setFormError('');
    try {
      const response = await webchatApi.post('/auth/otp/resend/', {
        email: validated.email,
        channel_id: String(id || '').slice(0, 64),
        channel_name: String(channel || '').slice(0, 120),
      });
      setChallengeId(response.data.challenge_id || '');
      setResendIn(RESEND_COOLDOWN_SECONDS);
      setOtpCode('');
    } catch (error) {
      const status = error.response?.status;
      setFormError(
        status === 429
          ? 'Please wait before requesting another code.'
          : "We couldn't resend the code. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = async () => {
    setContinueBusy(true);
    setFormError('');
    try {
      await continueAs();
      navigate(`/chat/${id}/${channel}`);
    } catch {
      setFormError('Your session expired. Please verify your email again.');
      setStep('identity');
    } finally {
      setContinueBusy(false);
    }
  };

  const handleUseDifferent = async () => {
    setContinueBusy(true);
    try {
      await dismissContinue();
    } finally {
      setContinueBusy(false);
      setStep('identity');
      setUsername('');
      setAcceptedTerms(false);
    }
  };

  const handleEmailChange = (e) => {
    // Strip control characters as the user types (injection prevention).
    //
    // no-control-regex is disabled deliberately rather than worked around: matching control
    // characters is the entire point here. This value reaches the backend and is interpolated
    // into an email header, where a bare CR or LF would let a crafted address inject headers.
    // The rule exists to catch control characters that appear in a pattern by accident; in this
    // one they are the target.
    const next = e.target.value
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f]/g, '')
      .slice(0, EMAIL_MAX_LENGTH);
    setUsername(next);
    if (formError) setFormError('');
  };

  const channelBlocked = channelState !== CHANNEL_STATES.READY;
  const channelSubtitle =
    channelState === CHANNEL_STATES.NOT_FOUND
      ? 'Chat link not available'
      : channelBlocked
        ? 'Chat temporarily unavailable'
        : step === 'otp'
          ? 'Check your email for a code'
          : step === 'continue'
            ? 'Welcome back'
            : 'Sign in to continue the conversation';

  const previewCopy =
    step === 'continue'
      ? 'Welcome back — continue with your saved session or use a different email.'
      : step === 'otp'
        ? `We sent a code to ${maskEmail(
            pendingEmail
          )}. Enter it below.`
        : 'Accept the terms below, then enter your email address to start chatting.';

  if (!bootstrapped) {
    return <LoginPageSkeleton />;
  }

  return (
    <div className="chat-app chat-anim-fade" style={theme.cssVars}>
      <div className="chat-shell">
        <ThemeToggle className="chat-header-toggle login-theme-toggle" />
        <section className="login-profile" aria-label="Channel profile">
          {isBootstrapping ? (
            <div className="login-profile-skeleton" aria-hidden="true">
              <span className="login-profile-avatar-skeleton" />
              <span className="login-profile-title-skeleton" />
              <span className="login-profile-subtitle-skeleton" />
            </div>
          ) : (
            <div className="login-profile-inner chat-anim-rise">
              <ChannelAvatar
                src={channelInfo?.profile_picture_url}
                name={channelName}
                className="login-profile-avatar"
                zoomable
              />
              <h1 className="login-profile-title">{channelName}</h1>
              <p className="login-profile-subtitle">{channelSubtitle}</p>
            </div>
          )}
        </section>

        <div className="chat-thread login-thread">
          {channelBlocked && channelFailure ? (
            <div className="login-channel-error chat-anim-fade" role="alert">
              <h2>{channelFailure.title}</h2>
              <p>{channelFailure.message}</p>
              {channelFailure.canRetry ? (
                <button
                  type="button"
                  className="login-retry-button"
                  onClick={() => setRetryAttempt((attempt) => attempt + 1)}
                >
                  <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                  Try again
                </button>
              ) : null}
            </div>
          ) : (
            <div className="login-thread-messages chat-anim-rise">
              <div className="chat-day-separator">
                <span>Today</span>
              </div>

              <div className="chat-bubble-row bot">
                <div
                  className={`chat-bubble bot ${bubbleRadiusClass(
                    false,
                    true,
                    false
                  )}`}
                >
                  Hi — welcome to {channelName}.
                </div>
              </div>

              <div className="chat-bubble-row user" style={{ marginTop: 10 }}>
                <div
                  className={`chat-bubble user ${bubbleRadiusClass(
                    true,
                    true,
                    true
                  )}`}
                >
                  Hello!
                  <div className="chat-bubble-meta">
                    <span>Just now</span>
                  </div>
                </div>
              </div>

              <div className="chat-bubble-row bot" style={{ marginTop: 10 }}>
                <div
                  className={`chat-bubble bot ${bubbleRadiusClass(
                    false,
                    true,
                    true
                  )}`}
                >
                  {previewCopy}
                  <div className="chat-bubble-meta">
                    <span>Just now</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sign-in composer — always at the bottom */}
        {!channelBlocked && step === 'continue' && (
          <div className="chat-bottom">
            <div className="login-continue-card chat-anim-rise">
              <p className="login-step-label">Continue as</p>
              <button
                type="button"
                className="login-continue-profile"
                onClick={handleContinue}
                disabled={continueBusy}
                style={{ borderColor: theme.accent }}
              >
                <span className="login-continue-avatar" aria-hidden="true">
                  {(continuePrompt?.username || '?')[0].toUpperCase()}
                </span>
                <span className="login-continue-meta">
                  <strong>
                    {continuePrompt?.masked_email ||
                      maskEmail(continuePrompt?.username || '')}
                  </strong>
                  <span>Saved session on this device</span>
                </span>
              </button>
              <button
                type="button"
                className="login-continue-other"
                onClick={handleUseDifferent}
                disabled={continueBusy}
              >
                Use a different email
              </button>
              {formError ? (
                <p className="chat-composer-error chat-anim-fade" role="alert">
                  {formError}
                </p>
              ) : null}
            </div>
          </div>
        )}

        {!channelBlocked && step === 'identity' && (
          <div className="chat-bottom">
            {isBootstrapping ? (
              <div className="login-composer-skeleton" aria-hidden="true">
                <span className="login-composer-skeleton-input" />
                <span className="login-composer-skeleton-send" />
              </div>
            ) : (
              <form onSubmit={handleIdentitySubmit} className="login-gate-form">
                <div
                  className={`login-terms${
                    !acceptedTerms
                      ? ' login-terms--pending'
                      : ' login-terms--accepted'
                  }${
                    termsHighlight && !acceptedTerms
                      ? ' login-terms--attention'
                      : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    id="login-terms"
                    className="login-terms-checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      setTermsHighlight(false);
                      if (e.target.checked) setFormError('');
                    }}
                    disabled={isSubmitting}
                    required
                    aria-describedby="login-step-hint"
                  />
                  <label htmlFor="login-terms" className="login-terms-label">
                    <span className="login-terms-step">Step 1</span>
                    I agree to the{' '}
                    <a
                      href="https://arifu.com/terms-and-conditions/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Terms and Conditions
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://arifu.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>

                <p className="login-step-label">
                  Step 2 — Enter your email address
                </p>

                <div className="chat-composer-row">
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={EMAIL_MAX_LENGTH}
                    placeholder="Email address"
                    value={username}
                    onChange={handleEmailChange}
                    required
                    disabled={isSubmitting}
                    className="chat-composer-input login-gate-input"
                    aria-label="Email address"
                    aria-invalid={Boolean(username.trim()) && !hasValidEmail}
                  />
                  <button
                    type="submit"
                    className={`chat-composer-send${
                      canSubmitIdentity ? '' : ' is-blocked'
                    }`}
                    style={{
                      backgroundColor: canSubmitIdentity
                        ? theme.accent
                        : undefined,
                    }}
                    aria-disabled={!canSubmitIdentity}
                    aria-label={
                      isSubmitting ? 'Sending code' : 'Send verification code'
                    }
                    onClick={(e) => {
                      if (canSubmitIdentity || isSubmitting) return;
                      e.preventDefault();
                      nudgeIncomplete();
                    }}
                  >
                    {isSubmitting ? (
                      <span className="chat-spinner" aria-hidden="true" />
                    ) : (
                      <PaperAirplaneIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {stepHint && !formError && (
                  <p
                    id="login-step-hint"
                    className="login-step-hint chat-anim-fade"
                  >
                    {stepHint}
                  </p>
                )}
                {formError && channelState === CHANNEL_STATES.READY && (
                  <p
                    className="chat-composer-error chat-anim-fade"
                    role="alert"
                  >
                    {formError}
                  </p>
                )}
              </form>
            )}
          </div>
        )}

        {!channelBlocked && step === 'otp' && (
          <div className="chat-bottom">
            <form onSubmit={handleOtpSubmit} className="login-gate-form">
              <p className="login-step-label">Enter your verification code</p>
              <div className="chat-composer-row">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="6-digit code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(normalizeOtpCode(e.target.value))}
                  disabled={isSubmitting}
                  className="chat-composer-input login-gate-input login-otp-input"
                  aria-label="Verification code"
                />
                <button
                  type="submit"
                  className={`chat-composer-send${
                    canSubmitOtp ? '' : ' is-blocked'
                  }`}
                  style={{
                    backgroundColor: canSubmitOtp ? theme.accent : undefined,
                  }}
                  aria-disabled={!canSubmitOtp}
                  aria-label={isSubmitting ? 'Verifying' : 'Verify code'}
                >
                  {isSubmitting ? (
                    <span className="chat-spinner" aria-hidden="true" />
                  ) : (
                    <PaperAirplaneIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              <div className="login-otp-actions">
                <button
                  type="button"
                  className="login-resend-btn"
                  onClick={handleResend}
                  disabled={resendIn > 0 || isSubmitting}
                >
                  {resendIn > 0
                    ? `Resend code in ${resendIn}s`
                    : 'Resend code'}
                </button>
                <button
                  type="button"
                  className="login-resend-btn"
                  onClick={() => {
                    setStep('identity');
                    setOtpCode('');
                    setFormError('');
                  }}
                  disabled={isSubmitting}
                >
                  Change email
                </button>
              </div>
              {stepHint && !formError && (
                <p className="login-step-hint chat-anim-fade">{stepHint}</p>
              )}
              {formError && (
                <p className="chat-composer-error chat-anim-fade" role="alert">
                  {formError}
                </p>
              )}
            </form>
          </div>
        )}
        <LoginFooter />
      </div>
    </div>
  );
}

export default Login;
